/* cart-drawer.js
   Full AJAX drawer: open/close, add-to-cart interception, qty updates, note save, savings & price formatting.
   Drop into theme assets and include before </body>.
*/
(function () {
  'use strict';

  const SELECTOR_ADD_FORM = 'form[action="/cart/add"], form[action$="/cart/add"]';
  const rootPath = (window.Shopify && Shopify.routes && Shopify.routes.root) ? Shopify.routes.root : '/';
  const CART_JSON = rootPath + 'cart.js';
  const ADD_JSON = rootPath + 'cart/add.js';
  const CHANGE_JSON = rootPath + 'cart/change.js';
  const UPDATE_JSON = rootPath + 'cart/update.js';

  // Utility
  function $(sel, root = document) { return root.querySelector(sel); }
  function $all(sel, root = document) { return Array.from(root.querySelectorAll(sel)); }
  function escapeHtml(s){ if (s == null) return ''; return String(s).replace(/&/g,'&amp;').replace(/"/g,'&quot;').replace(/</g,'&lt;').replace(/>/g,'&gt;'); }

  // ✅ Fixed: Format money in INR (₹)
  function formatMoney(cents) {
    if (typeof window.Shopify === 'object' && typeof Shopify.formatMoney === 'function') {
      try { 
        return Shopify.formatMoney(cents, "₹{{amount}}"); 
      } catch (e) { /* fallthrough */ }
    }
    const amount = (Number(cents) / 100);
    try {
      return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(amount);
    } catch (e) { 
      return "₹" + amount.toFixed(2); 
    }
  }

  // Drawer DOM
  const drawer = $('#CartDrawer');
  if (!drawer) return; // no drawer present
  const overlay = $('#CartDrawerOverlay') || drawer.querySelector('.cart-drawer__overlay');
  const closeBtn = $('#CartDrawerClose') || drawer.querySelector('.cart-drawer__close');
  const itemsContainer = $('#CartDrawerItems') || drawer.querySelector('.cart-drawer__items');
  const subtotalEl = $('#CartDrawerSubtotal') || drawer.querySelector('#CartDrawerSubtotal');
  const noteEl = $('#CartDrawerSpecialInstructions') || drawer.querySelector('#CartDrawerSpecialInstructions');

  // Ensure required elements exist
  if (!itemsContainer || !subtotalEl) {
    console.warn('Cart drawer: required DOM nodes missing (#CartDrawerItems or #CartDrawerSubtotal).');
  }

  // State
  const pendingUpdates = new Map(); // key -> boolean (lock)
  let noteSaveTimer = null;

  // Open/Close
  function openDrawer() {
    drawer.classList.add('active');
    if (overlay) overlay.classList.remove('hidden');
    // fetch latest cart
    fetchCart();
    // trap focus loosely: focus first focusable element
    setTimeout(() => {
      const first = drawer.querySelector('button, a, input, textarea, [tabindex]');
      if (first) first.focus();
    }, 200);
  }
  function closeDrawer() {
    drawer.classList.remove('active');
    if (overlay) overlay.classList.add('hidden');
  }
  if (closeBtn) closeBtn.addEventListener('click', (e) => { e.preventDefault(); closeDrawer(); });
  if (overlay) overlay.addEventListener('click', closeDrawer);

  // Open drawer on cart button click, prevent default navigation
  document.addEventListener('click', function (e) {
    const el = e.target.closest('.cartnavbuttonmain');
    if (!el) return;
    e.preventDefault();
    openDrawer();
  }, true);

  // Intercept add-to-cart forms so we show drawer instead of redirect
  document.addEventListener('submit', function (e) {
    const form = e.target;
    if (!form || form.nodeName !== 'FORM') return;
    // match forms that add to cart
    if (form.matches(SELECTOR_ADD_FORM) || form.querySelector(SELECTOR_ADD_FORM)) {
      e.preventDefault();
      handleAddToCart(form);
    }
  }, true);

  // Add-to-cart using Fetch (supports FormData)
  function handleAddToCart(form) {
    const submitButtons = Array.from(form.querySelectorAll('[type="submit"]'));
    submitButtons.forEach(b => b.disabled = true);

    const formData = new FormData(form);
    fetch(ADD_JSON, {
      method: 'POST',
      body: formData,
      credentials: 'same-origin'
    })
    .then(async res => {
      if (!res.ok) {
        try {
          const json = await res.json();
          console.error('Add to cart error:', json);
          alert((json && json.description) || 'Could not add to cart');
        } catch (err) {
          console.error('Add to cart error (no JSON):', err);
          alert('Could not add to cart');
        }
        return;
      }
      return res.json();
    })
    .then(() => {
      openDrawer();
    })
    .catch(err => {
      console.error('Add to cart request failed', err);
    })
    .finally(() => {
      submitButtons.forEach(b => b.disabled = false);
    });
  }

  // Fetch cart JSON and render
  function fetchCart(openWhenDone = false) {
    return fetch(CART_JSON, { credentials: 'same-origin' })
      .then(r => {
        if (!r.ok) throw new Error('Failed to fetch cart');
        return r.json();
      })
      .then(cart => {
        renderCart(cart);
        if (openWhenDone) openDrawer();
        return cart;
      })
      .catch(err => {
        console.error('Error fetching cart:', err);
      });
  }

  // Render cart into the drawer
  function renderCart(cart) {
    if (!cart || !itemsContainer) return;
    const items = cart.items || [];
    if (items.length === 0) {
      itemsContainer.innerHTML = `<div class="cart-drawer__empty">Your cart is empty.</div>`;
      subtotalEl.textContent = formatMoney(0);
      const oldSavings = drawer.querySelector('.cart-subtotal__savings');
      if (oldSavings) oldSavings.remove();
      if (noteEl) noteEl.value = cart.note || '';
      return;
    }

    let html = '';
    items.forEach(item => {
      const key = escapeHtml(item.key || item.id || '');
      const imageUrl = escapeHtml(item.image || (item.featured_image && item.featured_image.url) || '');
      const title = escapeHtml(item.product_title || item.title || '');
      const variant = escapeHtml(item.variant_title || '');
      const qty = Number(item.quantity || 0);
      const showOriginal = (typeof item.original_line_price !== 'undefined' && Number(item.original_line_price) > Number(item.line_price));
      const originalLine = showOriginal ? formatMoney(item.original_line_price) : '';
      const linePrice = formatMoney(item.line_price);

      html += `
        <div class="cart-item" data-key="${key}">
          <button class="cart-item__remove" data-key="${key}" aria-label="Remove item">✕</button>

          <div class="cart-item__media">
            ${imageUrl ? `<img src="${imageUrl}" alt="${title}" width="60" height="60">` : ''}
            <div class="cart-item__info">
              <div class="cart-item__title">${title}</div>
              ${variant ? `<div class="cart-item__variant">${variant}</div>` : ''}
              <div class="cart-item__price-wrap">
                ${showOriginal ? `<span class="cart-item__original-price">${originalLine}</span>` : ''}
                <span class="cart-item__price">${linePrice}</span>
              </div>

              <div class="qty-control" data-key="${key}">
                <button class="qty-btn qty-decrease" aria-label="Decrease quantity">−</button>
                <input class="qty-input" data-key="${key}" type="number" value="${qty}" min="0" />
                <button class="qty-btn qty-increase" aria-label="Increase quantity">+</button>
              </div>

              ${renderProperties(item.properties)}
            </div>
          </div>
        </div>
      `;
    });

    itemsContainer.innerHTML = html;

    // Update header cart count
    const headerCountEl = document.querySelector('.cart-count.header-bar__cart-count');
    if (headerCountEl) {
      headerCountEl.textContent = cart.item_count || 0;
      if (cart.item_count > 0) {
        headerCountEl.classList.remove('hidden-count');
      } else {
        headerCountEl.classList.add('hidden-count');
      }
    }

    subtotalEl.textContent = formatMoney(cart.total_price || 0);

    // Savings
    let savings = 0;
    if (typeof cart.original_total_price !== 'undefined' && Number(cart.original_total_price) > Number(cart.total_price)) {
      savings = Number(cart.original_total_price) - Number(cart.total_price);
    } else if (typeof cart.total_discount !== 'undefined' && Number(cart.total_discount) > 0) {
      savings = Number(cart.total_discount);
    } else if (typeof cart.total_discounts !== 'undefined' && Number(cart.total_discounts) > 0) {
      savings = Number(cart.total_discounts);
    }

    const existingSavingsEl = drawer.querySelector('.cart-subtotal__savings');
    if (savings > 0) {
      const savingsHtml = `<div class="cart-subtotal__savings" id="CartDrawerSavings">You’re saving ${formatMoney(savings)}</div>`;
      if (existingSavingsEl) {
        existingSavingsEl.innerHTML = `You’re saving ${formatMoney(savings)}`;
      } else {
        subtotalEl.insertAdjacentHTML('beforebegin', savingsHtml);
      }
    } else if (existingSavingsEl) {
      existingSavingsEl.remove();
    }

    if (noteEl) noteEl.value = cart.note || '';
  }

  function renderProperties(properties) {
    if (!properties) return '';
    try {
      if (typeof properties !== 'object') return '';
      const entries = Object.entries(properties).filter(([k, v]) => v !== '' && v !== null && !k.startsWith('_'));
      if (entries.length === 0) return '';
      return entries.map(([k, v]) => {
        if (typeof v === 'string' && v.indexOf('/uploads/') !== -1) {
          const fileName = escapeHtml(v.split('/').pop());
          return `<div class="cart-item__property"><strong>${escapeHtml(k)}:</strong> <a href="${escapeHtml(v)}" target="_blank">${fileName}</a></div>`;
        }
        return `<div class="cart-item__property"><strong>${escapeHtml(k)}:</strong> ${escapeHtml(v)}</div>`;
      }).join('');
    } catch (e) {
      return '';
    }
  }

  // Event delegation for qty / remove
  itemsContainer.addEventListener('click', function (e) {
    const plus = e.target.closest('.qty-increase');
    const minus = e.target.closest('.qty-decrease');
    const removeBtn = e.target.closest('.cart-item__remove');

    if (plus) {
      e.preventDefault();
      const itemEl = plus.closest('.cart-item');
      const input = itemEl.querySelector('.qty-input');
      const key = input.dataset.key;
      const newQty = (parseInt(input.value || '0', 10) || 0) + 1;
      setInputValue(input, newQty);
      updateQty(key, newQty);
      return;
    }

    if (minus) {
      e.preventDefault();
      const itemEl = minus.closest('.cart-item');
      const input = itemEl.querySelector('.qty-input');
      const key = input.dataset.key;
      let newQty = (parseInt(input.value || '0', 10) || 0) - 1;
      if (newQty < 1) newQty = 0;
      setInputValue(input, newQty);
      updateQty(key, newQty);
      return;
    }

    if (removeBtn) {
      e.preventDefault();
      const itemEl = removeBtn.closest('.cart-item');
      const key = itemEl.dataset.key;
      updateQty(key, 0);
      return;
    }
  });

  itemsContainer.addEventListener('change', function (e) {
    const input = e.target;
    if (!input.classList || !input.classList.contains('qty-input')) return;
    let newQty = parseInt(input.value || '0', 10);
    if (isNaN(newQty) || newQty < 1) newQty = 0;
    const key = input.dataset.key;
    setInputValue(input, newQty);
    updateQty(key, newQty);
  });

  function setInputValue(input, val) {
    try { input.value = val; } catch (e) { }
  }

  function updateQty(key, qty) {
    if (!key) return;
    if (pendingUpdates.get(key)) return;
    pendingUpdates.set(key, true);

    const itemEl = itemsContainer.querySelector(`.cart-item[data-key="${CSS.escape(key)}"]`);
    const controls = itemEl ? itemEl.querySelectorAll('button, input') : [];
    controls.forEach(c => c.disabled = true);

    fetch(CHANGE_JSON, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: key, quantity: qty })
    })
    .then(async res => {
      if (!res.ok) {
        try {
          const json = await res.json();
          console.error('Cart change error:', json);
        } catch (err) {
          console.error('Cart change failed', err);
        }
        return fetchCart();
      }
      return res.json();
    })
    .then(cartOrObj => {
      if (cartOrObj && cartOrObj.items) {
        renderCart(cartOrObj);
      } else {
        fetchCart();
      }
    })
    .catch(err => {
      console.error('Update qty request failed', err);
      fetchCart();
    })
    .finally(() => {
      pendingUpdates.delete(key);
      controls.forEach(c => c.disabled = false);
    });
  }

  if (noteEl) {
    noteEl.addEventListener('input', function () {
      if (noteSaveTimer) clearTimeout(noteSaveTimer);
      noteSaveTimer = setTimeout(() => {
        const payload = { note: noteEl.value || '' };
        fetch(UPDATE_JSON, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify(payload)
        })
        .then(res => {
          if (!res.ok) throw new Error('Failed to save note');
          return res.json();
        })
        .then(cart => {
          if (cart && cart.items) renderCart(cart);
        })
        .catch(err => {
          console.error('Error saving cart note', err);
        });
      }, 650);
    });
  }

  fetchCart(false);

})();
